//
//  QaProgramChecklistActivityDrawer.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the QA Program Checklist activity drawer.
//

import {
    ActionIcon,
    Alert,
    Badge,
    Box,
    Button,
    DataList,
    Divider,
    Drawer,
    Group,
    Paper,
    Stack,
    Tabs,
    Text,
    TextInput,
    Timeline,
    Title,
    Tooltip
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from "react";

import {
    QaProgramChecklistCompletionState,
    type SourceDocument,
    TrialQaProgramChecklistItemNote,
    type TrialQaProgramChecklistItemView
} from "src/api/Tmd.dtos";
import AppSettings from "src/AppSettings";
import Icon from "src/components/common/Icon";
import useCurrentUser from "src/hooks/authentication/useCurrentUser";
import { formatBytes } from "src/components/forms/inputs/FiledUploadInput.DefaultView";

import {
    buildQaProgramChecklistActivity,
    buildQaProgramChecklistAttachments,
    getQaProgramChecklistCompletionStateLabel
} from "./qaProgramChecklistActivity";
import QaProgramChecklistNoteComposer from "./QaProgramChecklistNoteComposer";

/** Supplies the task whose activity is shown and the mutation that appends notes to it. */
export interface QaProgramChecklistActivityDrawerProps {
    opened: boolean;
    onClose: () => void;
    item?: Pick<
        TrialQaProgramChecklistItemView,
        "id" | "task" | "notes" | "completionStateChanges" | "attachmentCount"
    >;
    categoryName?: string;
    canUpdate: boolean;
    onAddNote: (note: TrialQaProgramChecklistItemNote) => Promise<unknown>;
}

/**
 * Presents an activity entry's date, with its relative age behind a tooltip.
 *
 * Postconditions: A note that has not been saved yet reads New rather than an empty date.
 */
const QaProgramChecklistActivityDate: FC<{ dateEntered?: string }> = ({ dateEntered }) => {
    const dateInformation = useMemo(() => {
        if (!dateEntered) {
            return undefined;
        }

        const date = dayjs(dateEntered);
        const now = dayjs();

        return {
            date: date.format("DD/MM/YYYY"),
            relative: now.isAfter(date) ? date.fromNow() : date.toNow()
        };
    }, [dateEntered]);

    if (!dateInformation) {
        return (
            <Badge
                color="secondary.6"
                autoContrast
            >
                New
            </Badge>
        );
    }

    return (
        <Tooltip
            label={
                <DataList>
                    <DataList.Item>
                        <DataList.ItemLabel>Date</DataList.ItemLabel>
                        <DataList.ItemValue>{dateInformation.date}</DataList.ItemValue>
                    </DataList.Item>
                    <DataList.Item>
                        <DataList.ItemLabel>Relative</DataList.ItemLabel>
                        <DataList.ItemValue>{dateInformation.relative}</DataList.ItemValue>
                    </DataList.Item>
                </DataList>
            }
        >
            <Badge>{dateInformation.date}</Badge>
        </Tooltip>
    );
};

/** Supplies one activity entry's heading and the body rendered beneath it. */
interface QaProgramChecklistActivityCardProps {
    author: string;
    dateEntered?: string;
    action?: ReactNode;
    children: ReactNode;
}

/**
 * Renders one activity entry's chrome, whether it is immutable or a note being written.
 *
 * Postconditions: The supplied action sits left of the entry date, so the date stays the rightmost
 * thing in every heading. The body is the caller's, which is what lets a card hold either the
 * saved text or the add-only composer.
 */
const QaProgramChecklistActivityCard: FC<QaProgramChecklistActivityCardProps> = ({
    author,
    dateEntered,
    action,
    children
}) => (
    <Paper
        withBorder
        style={{ overflow: "hidden" }}
    >
        <Stack gap={0}>
            <Group
                bg="gray.2"
                p="sm"
                justify="space-between"
                wrap="nowrap"
            >
                <Title order={5}>{author || "Unknown"}</Title>
                <Group
                    gap="xs"
                    wrap="nowrap"
                >
                    {action}
                    <QaProgramChecklistActivityDate dateEntered={dateEntered} />
                </Group>
            </Group>
            <Divider />
            {children}
        </Stack>
    </Paper>
);

/** Renders one immutable attachment through the standard source-document endpoints. */
const QaProgramChecklistAttachment: FC<{
    attachment: SourceDocument;
    details?: ReactNode;
    action?: ReactNode;
}> = ({ attachment, details, action }) => {
    const viewUrl = attachment.id ? `/api/v2/source-document/${attachment.id}` : undefined;
    const downloadUrl = attachment.id
        ? `/api/v2/source-document/download/${attachment.id}`
        : undefined;

    return (
        <Group
            justify="space-between"
            wrap="nowrap"
            align="stretch"
            py="xs"
        >
            <Box
                style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    minWidth: 0
                }}
            >
                <Text
                    size="sm"
                    fw={500}
                    truncate
                >
                    {attachment.name || "Attachment"}
                </Text>
                <Text
                    size="xs"
                    c="dimmed"
                >
                    {[
                        attachment.contentType,
                        attachment.fileSize && formatBytes(attachment.fileSize, 1)
                    ]
                        .filter(Boolean)
                        .join(" · ")}
                </Text>
            </Box>
            <Stack
                gap={4}
                align="flex-end"
                justify="space-between"
                style={{ flexShrink: 0 }}
            >
                <Group
                    gap="xs"
                    wrap="nowrap"
                >
                    {action}
                    {viewUrl && (
                        <Tooltip
                            label="View attachment"
                            withArrow
                        >
                            <ActionIcon
                                component="a"
                                href={viewUrl}
                                target="_blank"
                                variant="subtle"
                                color="primary.6"
                                size="sm"
                                aria-label="View attachment"
                            >
                                <Icon icon={AppSettings.icons.view} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                    {downloadUrl && (
                        <Tooltip
                            label="Download attachment"
                            withArrow
                        >
                            <ActionIcon
                                component="a"
                                href={downloadUrl}
                                target="_blank"
                                variant="subtle"
                                color="primary.6"
                                size="sm"
                                aria-label="Download attachment"
                            >
                                <Icon icon={AppSettings.icons.download} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Group>
                {details}
            </Stack>
        </Group>
    );
};

/** Renders attachments beneath a note, with boundaries between the message and each file. */
const QaProgramChecklistNoteAttachments: FC<{ attachments?: SourceDocument[] }> = ({
    attachments = []
}) =>
    attachments.length > 0 ? (
        <Box>
            <Divider />
            {attachments.map((attachment, index) => (
                <Box key={attachment.id ?? index}>
                    {index > 0 && <Divider />}
                    <Box px="sm">
                        <QaProgramChecklistAttachment attachment={attachment} />
                    </Box>
                </Box>
            ))}
        </Box>
    ) : null;

/**
 * Presents a task's activity timeline over the checklist and accepts the next note.
 *
 * Postconditions: History interleaves immutable notes and completion changes; Attachments flattens
 * every note file newest-first. Adding a note uses the shared form uploader and refreshes both full
 * and pinned consumers through the caller's shared mutation.
 */
const QaProgramChecklistActivityDrawer: FC<QaProgramChecklistActivityDrawerProps> = ({
    opened,
    onClose,
    item,
    categoryName,
    canUpdate,
    onAddNote
}) => {
    const user = useCurrentUser();
    const [tab, setTab] = useState<string | null>("history");
    const [adding, setAdding] = useState(false);
    const [attachmentSearch, setAttachmentSearch] = useState("");
    const composerRef = useRef<HTMLDivElement>(null);
    const activities = useMemo(
        () =>
            buildQaProgramChecklistActivity(item?.notes ?? [], item?.completionStateChanges ?? []),
        [item?.completionStateChanges, item?.notes]
    );
    const attachments = useMemo(
        () => buildQaProgramChecklistAttachments(item?.notes, attachmentSearch),
        [attachmentSearch, item?.notes]
    );

    // Wait for the History panel to replace Attachments before revealing the newly mounted composer.
    useEffect(() => {
        if (!adding || tab !== "history") {
            return;
        }

        const frame = requestAnimationFrame(() => {
            composerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });

        return () => cancelAnimationFrame(frame);
    }, [adding, tab]);

    /** Abandons the current entry, dropping the draft card or restoring the saved text. */
    const stopComposing = () => {
        setAdding(false);
    };

    /** Clears the entry state alongside closing, so the next task opens on its record. */
    const close = () => {
        stopComposing();
        setTab("history");
        setAttachmentSearch("");
        onClose();
    };

    const addNote = async (note: TrialQaProgramChecklistItemNote) => {
        try {
            await onAddNote(note);
            stopComposing();
            notifications.show({ color: "success", message: "Checklist note added." });
        } catch (error) {
            notifications.show({
                color: "error",
                message: "The checklist note could not be added."
            });
            throw error;
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={close}
            position="right"
            size="lg"
            title="Activity"
            overlayProps={{ backgroundOpacity: 0.4, blur: 2 }}
            styles={{
                body: { padding: 0 },
                header: {
                    paddingLeft: "var(--mantine-spacing-sm)",
                    paddingRight: "var(--mantine-spacing-sm)"
                },
                title: {
                    color: "var(--mantine-color-primary-text)",
                    fontSize: "var(--mantine-h2-font-size)",
                    fontWeight: "var(--mantine-h2-font-weight)",
                    letterSpacing: "var(--mantine-h2-letter-spacing)",
                    lineHeight: "var(--mantine-h2-line-height)"
                }
            }}
        >
            <Box
                bg="gray.0"
                p="sm"
                style={{
                    borderTop: "1px solid var(--mantine-color-default-border)",
                    borderBottom: "1px solid var(--mantine-color-default-border)"
                }}
            >
                <Text fw={500}>{item?.task}</Text>
                {categoryName && (
                    <Text
                        size="sm"
                        c="dimmed"
                    >
                        {categoryName}
                    </Text>
                )}
            </Box>

            <Tabs
                value={tab}
                onChange={setTab}
            >
                <Tabs.List px="sm">
                    <Tabs.Tab value="history">History</Tabs.Tab>
                    <Tabs.Tab value="attachments">
                        Attachments ({item?.attachmentCount ?? 0})
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel
                    value="history"
                    p="sm"
                >
                    <Stack gap="sm">
                        {activities.length === 0 && !adding && (
                            <Alert
                                color="information"
                                icon={<Icon icon={AppSettings.icons.information} />}
                                title="No activity"
                            >
                                There is currently no activity associated with this task.
                            </Alert>
                        )}

                        {(activities.length > 0 || adding) && (
                            <Timeline bulletSize={28}>
                                {activities.map(activity => {
                                    if (activity.kind === "completion") {
                                        const completionState = activity.change.completionState;
                                        const color =
                                            completionState ===
                                            QaProgramChecklistCompletionState.Yes
                                                ? "success"
                                                : completionState ===
                                                    QaProgramChecklistCompletionState.NotApplicable
                                                  ? "gray"
                                                  : "secondary.6";

                                        return (
                                            <Timeline.Item
                                                key={activity.key}
                                                bullet={
                                                    <Icon
                                                        icon={AppSettings.icons.previousVersion}
                                                    />
                                                }
                                            >
                                                <QaProgramChecklistActivityCard
                                                    author={activity.author}
                                                    dateEntered={activity.dateEntered}
                                                >
                                                    <Group
                                                        p="sm"
                                                        gap="xs"
                                                    >
                                                        <Text size="sm">Completion changed to</Text>
                                                        <Badge
                                                            color={color}
                                                            autoContrast
                                                        >
                                                            {getQaProgramChecklistCompletionStateLabel(
                                                                completionState
                                                            )}
                                                        </Badge>
                                                    </Group>
                                                </QaProgramChecklistActivityCard>
                                            </Timeline.Item>
                                        );
                                    }

                                    const note = activity.note;
                                    return (
                                        <Timeline.Item
                                            key={activity.key}
                                            bullet={<Icon icon={AppSettings.icons.comments} />}
                                        >
                                            <QaProgramChecklistActivityCard
                                                author={note.enteredBy}
                                                dateEntered={note.dateEntered}
                                            >
                                                <Text
                                                    p="sm"
                                                    size="sm"
                                                    style={{
                                                        overflowWrap: "anywhere",
                                                        whiteSpace: "pre-wrap"
                                                    }}
                                                >
                                                    {note.comments}
                                                </Text>
                                                <QaProgramChecklistNoteAttachments
                                                    attachments={note.attachments}
                                                />
                                            </QaProgramChecklistActivityCard>
                                        </Timeline.Item>
                                    );
                                })}

                                {adding && item?.id && (
                                    <Timeline.Item
                                        bullet={<Icon icon={AppSettings.icons.comments} />}
                                    >
                                        <Box ref={composerRef}>
                                            <QaProgramChecklistActivityCard
                                                author={user?.username ?? ""}
                                            >
                                                <QaProgramChecklistNoteComposer
                                                    itemId={item.id}
                                                    onCancel={stopComposing}
                                                    onAdd={addNote}
                                                />
                                            </QaProgramChecklistActivityCard>
                                        </Box>
                                    </Timeline.Item>
                                )}
                            </Timeline>
                        )}

                        {canUpdate && !adding && (
                            <Group justify="flex-end">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setTab("history");
                                        setAdding(true);
                                    }}
                                >
                                    Add note
                                </Button>
                            </Group>
                        )}
                    </Stack>
                </Tabs.Panel>

                <Tabs.Panel
                    value="attachments"
                    p="sm"
                >
                    <Stack gap="sm">
                        <TextInput
                            value={attachmentSearch}
                            onChange={event => setAttachmentSearch(event.currentTarget.value)}
                            placeholder="Search filenames or note messages"
                            aria-label="Search attachments"
                        />
                        {attachments.length === 0 ? (
                            <Alert
                                color="information"
                                icon={<Icon icon={AppSettings.icons.information} />}
                                title="No attachments"
                            >
                                No checklist note attachments match this search.
                            </Alert>
                        ) : (
                            attachments.map(({ attachment, note }) => (
                                <Paper
                                    key={attachment.id}
                                    withBorder
                                    style={{ overflow: "hidden" }}
                                >
                                    <Box px="sm">
                                        <QaProgramChecklistAttachment
                                            attachment={attachment}
                                            details={
                                                <Text
                                                    size="xs"
                                                    c="dimmed"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    {note.enteredBy || "Unknown"} ·{" "}
                                                    {dayjs(note.dateEntered).format("DD/MM/YYYY")}
                                                </Text>
                                            }
                                            action={
                                                <Tooltip
                                                    withArrow
                                                    multiline
                                                    maw={320}
                                                    label={
                                                        <Text
                                                            size="sm"
                                                            style={{
                                                                overflowWrap: "anywhere",
                                                                whiteSpace: "pre-wrap"
                                                            }}
                                                        >
                                                            {note.comments}
                                                        </Text>
                                                    }
                                                >
                                                    <ActionIcon
                                                        variant="subtle"
                                                        color="primary.6"
                                                        size="sm"
                                                        aria-label="View associated note"
                                                    >
                                                        <Icon icon={AppSettings.icons.comments} />
                                                    </ActionIcon>
                                                </Tooltip>
                                            }
                                        />
                                    </Box>
                                </Paper>
                            ))
                        )}
                        {canUpdate && !adding && (
                            <Group justify="flex-end">
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        setTab("history");
                                        setAdding(true);
                                    }}
                                >
                                    Add note
                                </Button>
                            </Group>
                        )}
                    </Stack>
                </Tabs.Panel>
            </Tabs>
        </Drawer>
    );
};

export default QaProgramChecklistActivityDrawer;
